/**
 * The geometry behind the "Recover from classification" slider: a vertical list of every
 * classification in the stack, highest at the top and lowest at the bottom, magnified around the
 * pointer so forty-odd rows fit in one column and the ones you are pointing at are readable.
 *
 * A dropdown of 44 grouped classifications could not show the thing the choice actually means —
 * "this band and everything ABOVE it is recovered" — and made you scroll a menu to find a band
 * you could not see in context (Andrew, 2026-09-21). A vertical column in stack order shows the
 * cut, and the magnification is what makes that column usable at this size.
 *
 * The magnification is a graphical fisheye (Sarkar & Brown): the focus point maps to itself and
 * everything else is pushed away from it, expanding near the focus and compressing toward the
 * ends. The property that matters is that the focus is FIXED — so the row under the pointer in
 * the undistorted layout is still the row under the pointer after distortion. A dock-style
 * "enlarge rows near the cursor then re-stack them" does not have that property: the enlarged
 * rows push their neighbours, the row under the pointer changes as the pointer stands still, and a
 * click lands on a different classification from the one that was highlighted.
 */

export interface SliderClass {
    id: number | string
    Name: string
    Category?: string | null
    DisplayRank: number | string
    /** The classification's own tint — the colour its band has on the canvas. */
    BackgroundColor?: string | null
}

/** A run of consecutive rows sharing a category, in stack order. */
export interface SliderBand {
    start: number
    end: number
    category: string
    colour: string | null
}

/**
 * The category bands down the column: each maximal run of rows with the same Category, taking
 * the colour of its highest classification. Runs, not groups — a category that reappears lower
 * in the stack is a second band, because the column is in stack order and a band that jumped
 * would draw one colour across rows that are not together.
 */
export function bandsForSlider<T extends SliderClass>(rows: T[]): SliderBand[] {
    const bands: SliderBand[] = []
    rows.forEach((r, i) => {
        const category = r.Category || ''
        const last = bands[bands.length - 1]
        if (last && last.category === category) last.end = i
        else bands.push({ start: i, end: i, category, colour: r.BackgroundColor || null })
    })
    return bands
}

/** One laid-out row: where it starts, how tall it is, both in pixels from the top. */
export interface FisheyeRow {
    top: number
    height: number
}

/** How strongly the region at the focus is enlarged: a row there draws (d + 1) times its share. */
export const FISHEYE_DISTORTION = 4

/**
 * Stack order for the slider: highest DisplayRank first, so index 0 is drawn at the TOP. Ties are
 * broken by name so the order is stable between renders.
 */
export function orderForSlider<T extends SliderClass>(classes: T[]): T[] {
    return [...classes].sort((a, b) =>
        Number(b.DisplayRank) - Number(a.DisplayRank) || String(a.Name).localeCompare(String(b.Name)))
}

/** Sarkar–Brown fisheye along one axis: `p` moved away from focus `f` within [0, length]. */
export function fisheye(p: number, f: number, length: number, d: number = FISHEYE_DISTORTION): number {
    if (p === f || length <= 0) return p
    const boundary = p > f ? length : 0
    const span = boundary - f
    if (span === 0) return p
    const x = (p - f) / span                       // 0 at the focus, 1 at the boundary
    const xd = ((d + 1) * x) / (d * x + 1)
    return f + xd * span
}

/**
 * Every row's top and height for `count` rows in `length` pixels, magnified around `focus`
 * (a pixel offset from the top). Heights always sum to `length` exactly — nothing overflows the
 * column and nothing is left over — because the row boundaries are the distorted images of evenly
 * spaced points, and the two ends map to themselves.
 */
export function fisheyeLayout(count: number, length: number, focus: number, d: number = FISHEYE_DISTORTION): FisheyeRow[] {
    if (count <= 0) return []
    const f = Math.min(Math.max(focus, 0), length)
    const step = length / count
    const edges = Array.from({ length: count + 1 }, (_, k) => fisheye(k * step, f, length, d))
    return Array.from({ length: count }, (_, k) => ({ top: edges[k], height: edges[k + 1] - edges[k] }))
}

/** Which row sits under a pixel offset, in the undistorted layout — and so, by construction, in the distorted one too. */
export function rowAt(count: number, length: number, offset: number): number {
    if (count <= 0 || length <= 0) return -1
    return Math.min(count - 1, Math.max(0, Math.floor((offset / length) * count)))
}

/** The centre of row `index` in the undistorted layout: where to rest the magnification. */
export function rowCentre(count: number, length: number, index: number): number {
    return ((index + 0.5) / count) * length
}
